import React from 'react';
import {
  CheckCircle2, Loader2, PauseCircle, XCircle, MinusCircle, Circle,
} from 'lucide-react';
import { StageResult } from '../../services/pipelineService';

// Four plain-language phases mapped onto the underlying pipeline stages.
const PHASES = [
  { label: 'Reading your document', stages: ['ingest', 'classify', 'extract'] },
  { label: 'Checking every value', stages: ['eval'] },
  { label: 'Your review', stages: ['sanity_check'] },
  { label: 'Ready', stages: ['export'] },
];

function iconFor(status: string) {
  switch (status) {
    case 'succeeded': return <CheckCircle2 size={20} color="var(--mx-success)" />;
    case 'running': return <Loader2 size={20} color="var(--mx-accent)" className="mx-spin" />;
    case 'paused': return <PauseCircle size={20} color="var(--mx-warning)" />;
    case 'failed': return <XCircle size={20} color="var(--mx-danger)" />;
    case 'cancelled':
    case 'skipped': return <MinusCircle size={20} color="var(--mx-text-subtle)" />;
    default: return <Circle size={20} color="var(--mx-border-strong)" />;
  }
}

const Stepper: React.FC<{ stages: StageResult[] }> = ({ stages }) => {
  // Canonical status per underlying stage: prefer the non-stream entry, else any entry.
  const statusOf = (name: string) => {
    const entries = stages.filter((s) => s.stage === name);
    const primary = entries.find((s) => !s.stream) || entries[0];
    return primary?.status || 'pending';
  };
  const msOf = (name: string) => {
    const e = stages.find((s) => s.stage === name && s.started_at && s.finished_at);
    if (!e || !e.started_at || !e.finished_at) return 0;
    const ms = new Date(e.finished_at).getTime() - new Date(e.started_at).getTime();
    return ms > 0 ? ms : 0;
  };
  // Combine the constituent stage statuses into one phase status.
  const phaseStatus = (names: string[]) => {
    const sts = names.map(statusOf);
    if (sts.includes('failed')) return 'failed';
    if (sts.includes('paused')) return 'paused';
    if (sts.includes('running')) return 'running';
    if (sts.every((s) => s === 'succeeded')) return 'succeeded';
    if (sts.some((s) => s === 'succeeded')) return 'running';  // partially done → in progress
    return 'pending';
  };
  const fmt = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : ms > 0 ? `${ms}ms` : '');

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {PHASES.map((p, i) => {
        const status = phaseStatus(p.stages);
        const done = status === 'succeeded';
        const dur = fmt(p.stages.reduce((a, n) => a + msOf(n), 0));
        return (
          <React.Fragment key={p.label}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
              flex: '0 0 auto', width: 108 }}>
              {iconFor(status)}
              <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 6, textAlign: 'center', lineHeight: 1.25,
                color: status === 'pending' ? 'var(--mx-text-subtle)' : 'var(--mx-text)' }}>
                {p.label}
              </div>
              <div className="mx-subtle" style={{ fontSize: 11, height: 14 }}>{dur}</div>
            </div>
            {i < PHASES.length - 1 && (
              <div style={{ flex: 1, height: 2, marginTop: 10, borderRadius: 2,
                background: done ? 'var(--mx-success)' : 'var(--mx-border)' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Stepper;
